import React, { useEffect, useRef } from "react";
import { useMap } from "react-map-gl";
import { useMapboxDraw } from "../DrawToolProvider";
import { useSelector, useDispatch } from "react-redux";
import { inputsActions } from "../..";

const mapMenuItems = [
  {
    label: "Draw",
    action: "draw_polygon",
  },
  {
    label: "Toggle Dims On",
    action: "toggle_dims",
  },
  {
    label: "Toggle Azi On",
    action: "toggle_azi",
  },
  {
    label: "Export Map",
    action: null,
    submenu: [
      {
        label: "Export to KMZ",
        action: "export_kmz",
      },
      {
        label: "Export to CAD",
        action: "export_cad",
      },
    ],
  },
];

const MapboxContext = ({ menuItems = mapMenuItems }) => {
  const dispatch = useDispatch();
  const { current: mapRef } = useMap();
  const map = mapRef.getMap();
  const draw = useMapboxDraw();

  const site_features = useSelector((state) => state.inputs.site_features);
  const site_features_ref = useRef();

  useEffect(() => {
    site_features_ref.current = site_features;
  }, [site_features]);

  const contextMenuRef = useRef(null);
  const contextMenuWrapperRef = useRef(null);

  useEffect(() => {
    if (!map) return;

    const contextMenu = document.createElement("section");
    contextMenu.className = "context-menu";

    document.body.appendChild(contextMenu);
    contextMenuRef.current = contextMenu;

    const handleClick = (e) => {
      const target = e.target;
      if (target && target.dataset.action) {
        handleMenuAction(target.dataset, draw);
        contextMenu.style.display = "none";
      }
    };

    const handleOutsideClick = (e) => {
      if (contextMenuWrapperRef.current && !contextMenuWrapperRef.current.contains(e.target)) {
        contextMenu.style.display = "none";
      }
    };

    contextMenu.addEventListener("click", handleClick);
    document.addEventListener("click", handleOutsideClick);

    map.on("contextmenu", (e) => {
      e.preventDefault();
      const { clientX: x, clientY: y } = e.originalEvent;
      const point = [e.point.x, e.point.y];

      const features = map.queryRenderedFeatures(point);
      let clicked_features_arr = [];

      if (features.length > 0) {
        features.forEach((feature) =>
          clicked_features_arr.push({
            label: `Polygon ${feature.properties.id.slice(0, 2)}`,
            highLight: true,
            action: null,
            id: feature.properties.id,
            submenu: [
              { label: "Offset Polygon", action: "offset_poly", id: feature.properties.id },
              { label: "Set as Boundary", action: "set_as_boundary", id: feature.properties.id },
              { label: "Set as Exclusion", action: "set_as_exclusion", id: feature.properties.id },
              { label: "Set as Inactive", action: "set_as_inactive", id: feature.properties.id },
              { label: "Delete", action: "delete_poly", id: feature.properties.id },
            ],
          })
        );
      }

      contextMenu.innerHTML = renderMenuItems([...menuItems, ...clicked_features_arr]);

      // Attach hover events to menu items after rendering
      attachHoverEventsToMenuItems(contextMenu, map);

      contextMenu.style.left = `${x}px`;
      contextMenu.style.top = `${y}px`;
      contextMenu.style.display = "block";
    });

    return () => {
      document.body.removeChild(contextMenu);
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [map, menuItems, draw]);

  const renderMenuItems = (items) => {
    return `
      <ul>
        ${items
          .map(
            (item) => `
          <li data-action="${item.action || ""}" data-id="${item.id || ""}" class="${item.submenu ? "has-submenu" : ""}">
            ${item.label}
            ${item.submenu ? `${renderMenuItems(item.submenu)}` : ""}
          </li>
        `
          )
          .join("")}
      </ul>
    `;
  };
  const attachHoverEventsToMenuItems = (contextMenu, map) => {
    const menuItems = contextMenu.querySelectorAll("li[data-id]");

    menuItems.forEach((menuItem) => {
      const id = menuItem.getAttribute("data-id");

      if (id) {
        menuItem.addEventListener("mouseenter", () => handleMenuItemHover(id, map));
        menuItem.addEventListener("mouseleave", () => handleMenuItemUnhover(id, map));
      }
    });
  };

  const handleMenuItemHover = (id, map) => {
    if (map && id) {
      dispatch(inputsActions.update_input("cur_hover_id", id));
    }
  };

  const handleMenuItemUnhover = (id, map) => {
    if (map && id) {
      dispatch(inputsActions.update_input("cur_hover_id", undefined));
    }
  };

  const updateIdentity = (id, identity) => {
    let feature = site_features_ref.current[id];
    if (feature) {
      feature.properties.identity = identity;
      feature.properties.active = identity === 0 ? !feature.properties.active : true;
      dispatch(inputsActions.update_site_input("edit_site_feature", feature));
    }
  };

  const deleteFeature = (id) => {
    if (!id) return;
    dispatch(inputsActions.update_site_input("remove_site_feature", id));
    draw.delete(id);
  };

  const handleMenuAction = (dataset, draw = undefined) => {
    switch (dataset.action) {
      case "draw_polygon":
        draw.changeMode(dataset.action);
        break;
      case "toggle_dims":
        console.log("toggle a thing", dataset.action);
        break;
      case "toggle_azi":
        console.log("toggle a thing", dataset.action);
        break;
      case "offset_poly":
        map.fire("toggleOffset", { id: dataset.id, selected_from: "context" });
        break;
      case "set_as_boundary":
        updateIdentity(dataset.id, 1);
        break;
      case "set_as_exclusion":
        updateIdentity(dataset.id, 2);
        break;
      case "set_as_inactive":
        updateIdentity(dataset.id, 0);
        break;
      case "delete_poly":
        deleteFeature(dataset.id);
        break;
      default:
        break;
    }
  };

  return <div ref={contextMenuWrapperRef} />;
};

export { MapboxContext };
